import { useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { MapPin } from 'lucide-react';

/**
 * Combobox for Colombian cities backed by GET /api/geo/cities.
 * Debounced query, keyboard navigation, and click-outside dismiss. Uncontrolled
 * with respect to fetching (fetches on every keystroke ≥1 char), but controlled
 * from the parent for the input value.
 */
export const CityCombobox = ({
  value = '',
  onChange,
  placeholder = 'Ciudad',
  testId = 'city-combobox',
  className = '',
}) => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const timerRef = useRef(null);

  const search = (q) => {
    api.get('/geo/cities', { params: { q, limit: 8 } })
      .then(({ data }) => { setItems(data.items || []); setOpen(true); })
      .catch(() => setItems([]));
  };

  const onInput = (e) => {
    const v = e.target.value;
    onChange?.(v);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => search(v), 180);
  };

  const onFocus = () => {
    if (!items.length) search(value);
    else setOpen(true);
  };

  const pick = (city) => {
    onChange?.(city);
    setOpen(false);
    setItems([]);
    inputRef.current?.blur();
  };

  useEffect(() => {
    const onDocClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const onKey = (e) => {
    if (!open || !items.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => (i + 1) % items.length); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => (i - 1 + items.length) % items.length); }
    else if (e.key === 'Enter') { e.preventDefault(); pick(items[active]); }
    else if (e.key === 'Escape') setOpen(false);
  };

  return (
    <div ref={containerRef} className={`relative ${className}`} data-testid={testId}>
      <MapPin className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 pointer-events-none z-10" />
      <Input
        ref={inputRef}
        data-testid={`${testId}-input`}
        value={value || ''}
        onChange={onInput}
        onFocus={onFocus}
        onKeyDown={onKey}
        placeholder={placeholder}
        autoComplete="off"
        className="pl-9 rounded-none"
      />
      {open && items.length > 0 && (
        <ul
          data-testid={`${testId}-options`}
          className="absolute z-40 top-full left-0 right-0 mt-1 max-h-64 overflow-auto bg-white border border-slate-200 shadow-lg"
        >
          {items.map((c, i) => (
            <li
              key={c}
              data-testid={`${testId}-option-${i}`}
              onMouseDown={(e) => { e.preventDefault(); pick(c); }}
              onMouseEnter={() => setActive(i)}
              className={`px-3 py-2 text-sm cursor-pointer flex items-center gap-2 ${
                i === active ? 'bg-orange-50 text-orange-900' : 'text-slate-700 hover:bg-slate-50'
              }`}
            >
              <MapPin className="h-3.5 w-3.5 text-slate-400" />
              {c}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
